import $ from 'jquery'
import 'slick-carousel'
import store from 'store'

class RedesignNavMobile {
  constructor (root) {
    this.root = root
    this.headerRoot = document.querySelector('.redesign-header')
    this.trigger = document.querySelector('[data-mobile-nav-trigger]')
    this.topTabs = Array.from(this.root.querySelectorAll('[data-top-tab]'))
    this.topPanels = Array.from(this.root.querySelectorAll('[data-panel-for]'))
    this.featuredSliders = Array.from(this.root.querySelectorAll('[data-mobile-featured-slider]'))
    this.sliderRaf = null
    this.previouslyFocused = null
    this.isOpen = false
    this.mediaQuery = window.matchMedia('(min-width: 1280px)')
    this.focusableSelector = 'button:not([disabled]):not([hidden]), a[href], input:not([disabled]):not([hidden]), select:not([disabled]):not([hidden]), textarea:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])'
    this.setOffset = this.setOffset.bind(this)
    this.onWindowResize = this.onWindowResize.bind(this)
    this.onTriggerClick = this.onTriggerClick.bind(this)
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this)
    this.onRootClick = this.onRootClick.bind(this)
    this.onRootKeydown = this.onRootKeydown.bind(this)
    this.onOverlayOpen = this.onOverlayOpen.bind(this)
    this.onMediaChange = this.onMediaChange.bind(this)
    this.onOpenLevelRequest = this.onOpenLevelRequest.bind(this)
    this.onSliderChange = this.onSliderChange.bind(this)
  }

  init () {
    if (!this.root || !this.trigger) return

    this.resetTabs()
    this.trigger.addEventListener('click', this.onTriggerClick)
    this.root.addEventListener('click', this.onRootClick)
    this.root.addEventListener('keydown', this.onRootKeydown)
    document.addEventListener('keydown', this.onDocumentKeydown)
    document.addEventListener('redesign:overlay-open', this.onOverlayOpen)
    document.addEventListener('redesign:mobile-nav-open-level', this.onOpenLevelRequest)
    window.addEventListener('resize', this.onWindowResize)
    window.addEventListener('scroll', this.setOffset, { passive: true })

    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', this.onMediaChange)
    } else if (typeof this.mediaQuery.addListener === 'function') {
      this.mediaQuery.addListener(this.onMediaChange)
    }
  }

  destroy () {
    if (this.sliderRaf) {
      window.cancelAnimationFrame(this.sliderRaf)
      this.sliderRaf = null
    }

    this.destroySliders()

    if (this.trigger) {
      this.trigger.removeEventListener('click', this.onTriggerClick)
    }

    this.root.removeEventListener('click', this.onRootClick)
    this.root.removeEventListener('keydown', this.onRootKeydown)
    document.removeEventListener('keydown', this.onDocumentKeydown)
    document.removeEventListener('redesign:overlay-open', this.onOverlayOpen)
    document.removeEventListener('redesign:mobile-nav-open-level', this.onOpenLevelRequest)
    window.removeEventListener('resize', this.onWindowResize)
    window.removeEventListener('scroll', this.setOffset)

    if (typeof this.mediaQuery.removeEventListener === 'function') {
      this.mediaQuery.removeEventListener('change', this.onMediaChange)
    } else if (typeof this.mediaQuery.removeListener === 'function') {
      this.mediaQuery.removeListener(this.onMediaChange)
    }

    this.close({ restoreFocus: false, skipHidden: true })
  }

  open () {
    if (this.mediaQuery.matches || this.isOpen) return

    this.previouslyFocused = document.activeElement
    this.setOffset()
    this.root.hidden = false
    this.isOpen = true
    document.documentElement.classList.add('redesign-menu-open')
    document.body.classList.add('redesign-menu-open')

    if (this.headerRoot) {
      this.headerRoot.classList.add('is-mobile-nav-open')
    }

    this.closeOtherOverlays()
    this.syncTriggerState()
    document.dispatchEvent(new window.CustomEvent('redesign:overlay-open', {
      detail: { source: 'mobile-nav' }
    }))

    window.requestAnimationFrame(() => {
      if (!this.isOpen) return

      this.setOffset()
      this.root.setAttribute('aria-hidden', 'false')
      this.focusActiveTopTab()
      this.refreshActiveSliders()
    })
  }

  close (options = {}) {
    const restoreFocus = typeof options.restoreFocus === 'undefined' ? true : options.restoreFocus
    const skipHidden = options.skipHidden === true

    if (!this.isOpen && !skipHidden) return

    if (this.sliderRaf) {
      window.cancelAnimationFrame(this.sliderRaf)
      this.sliderRaf = null
    }

    this.isOpen = false
    this.root.setAttribute('aria-hidden', 'true')
    document.documentElement.classList.remove('redesign-menu-open')
    document.body.classList.remove('redesign-menu-open')

    if (this.headerRoot) {
      this.headerRoot.classList.remove('is-mobile-nav-open')
    }

    this.resetTabs()
    this.syncTriggerState()

    if (!skipHidden) {
      window.setTimeout(() => {
        if (this.root.getAttribute('aria-hidden') === 'true') {
          this.root.hidden = true
        }
      }, 300)
    } else {
      this.root.hidden = true
    }

    if (restoreFocus && this.previouslyFocused && typeof this.previouslyFocused.focus === 'function') {
      this.previouslyFocused.focus()
    }

    this.previouslyFocused = null
  }

  setOffset () {
    if (!this.headerRoot) return

    const headerRect = this.headerRoot.getBoundingClientRect()
    const offset = Math.max(headerRect.bottom, 0)
    const height = Math.max(window.innerHeight - offset, 0)

    this.root.style.setProperty('--redesign-mobile-nav-offset', `${offset}px`)
    this.root.style.setProperty('--redesign-mobile-nav-height', `${height}px`)
  }

  activateTopTab (tab, options = {}) {
    if (!tab || !this.topTabs.includes(tab)) return

    const shouldFocus = options.focus === true
    const tabHandle = tab.dataset.topTab

    this.topTabs.forEach((topTab) => {
      const isActive = topTab === tab

      topTab.setAttribute('aria-selected', isActive ? 'true' : 'false')
      topTab.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    this.topPanels.forEach((panel) => {
      const isActive = panel.dataset.panelFor === tabHandle

      panel.hidden = !isActive

      if (isActive) {
        this.resetSubTabs(panel)
      }
    })

    if (shouldFocus) {
      tab.focus()
    }

    if (this.isOpen) {
      this.refreshActiveSliders()
    }
  }

  activateSubTab (tab, options = {}) {
    if (!tab) return

    const topPanel = tab.closest('[data-panel-for]')

    if (!topPanel) return

    const shouldFocus = options.focus === true
    const tabHandle = tab.dataset.subTab
    const subTabs = Array.from(topPanel.querySelectorAll('[data-sub-tab]'))
    const subPanels = Array.from(topPanel.querySelectorAll('[data-sub-panel-for]'))

    if (!subTabs.includes(tab)) return

    subTabs.forEach((subTab) => {
      const isActive = subTab === tab

      subTab.setAttribute('aria-selected', isActive ? 'true' : 'false')
      subTab.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    subPanels.forEach((panel) => {
      panel.hidden = panel.dataset.subPanelFor !== tabHandle
    })

    if (shouldFocus) {
      tab.focus()
    }

    if (this.isOpen) {
      this.refreshActiveSliders()
    }
  }

  isSliderInitialized (sliderElement) {
    return $(sliderElement).hasClass('slick-initialized')
  }

  applyLastSlideAlignment (sliderElement, slickInstance, currentSlide, nextSlide) {
    if (!sliderElement || !slickInstance) return

    const track = sliderElement.querySelector('.slick-track')

    if (!track) return

    const currentIndex = Number.isInteger(currentSlide)
      ? currentSlide
      : slickInstance.currentSlide
    const nextIndex = Number.isInteger(nextSlide)
      ? nextSlide
      : slickInstance.currentSlide

    track.style.marginLeft = ''

    if (nextIndex !== slickInstance.slideCount - 1) return

    const list = sliderElement.querySelector('.slick-list')
    const targetSlide = sliderElement.querySelector(`[data-slick-index="${nextIndex}"]`)

    if (!list || !targetSlide) return

    const gutter = parseFloat(window.getComputedStyle(list).paddingLeft) || 0
    const listRect = list.getBoundingClientRect()
    const slideRect = targetSlide.getBoundingClientRect()
    const currentLeft = Number.isFinite(slickInstance.currentLeft)
      ? slickInstance.currentLeft
      : slickInstance.getLeft(currentIndex)
    const trackShift = slickInstance.getLeft(nextIndex) - currentLeft
    const targetRight = slideRect.right + trackShift
    const offset = listRect.right - gutter - targetRight

    track.style.marginLeft = offset > 0 ? `${offset}px` : ''
  }

  onSliderChange (event, slickInstance, currentSlide, nextSlide) {
    this.applyLastSlideAlignment(
      event.currentTarget,
      slickInstance,
      currentSlide,
      nextSlide
    )
  }

  initSlider (sliderElement) {
    if (!sliderElement || this.isSliderInitialized(sliderElement) || sliderElement.children.length <= 1) return

    const slider = $(sliderElement)

    slider.on('init beforeChange', this.onSliderChange)
    slider.slick({
      arrows: false,
      dots: false,
      infinite: false,
      mobileFirst: true,
      slidesToScroll: 1,
      slidesToShow: 1.36,
      swipeToSlide: true
    })
  }

  refreshActiveSliders () {
    if (!this.isOpen || this.mediaQuery.matches) return

    if (this.sliderRaf) {
      window.cancelAnimationFrame(this.sliderRaf)
    }

    this.sliderRaf = window.requestAnimationFrame(() => {
      this.sliderRaf = null

      if (!this.isOpen || this.mediaQuery.matches) return

      this.featuredSliders.forEach((sliderElement) => {
        if (sliderElement.closest('[hidden]')) return

        this.initSlider(sliderElement)

        if (this.isSliderInitialized(sliderElement)) {
          const slider = $(sliderElement)

          slider.slick('setPosition')
          this.applyLastSlideAlignment(sliderElement, slider.slick('getSlick'))
        }
      })
    })
  }

  destroySliders () {
    this.featuredSliders.forEach((sliderElement) => {
      const slider = $(sliderElement)

      slider.off('init beforeChange', this.onSliderChange)

      if (this.isSliderInitialized(sliderElement)) {
        slider.slick('unslick')
      }
    })
  }

  onWindowResize () {
    this.setOffset()

    if (this.isOpen) {
      this.refreshActiveSliders()
    }
  }

  resetSubTabs (topPanel) {
    if (!topPanel) return

    const subTabs = Array.from(topPanel.querySelectorAll('[data-sub-tab]'))
    const subPanels = Array.from(topPanel.querySelectorAll('[data-sub-panel-for]'))
    const firstSubTab = subTabs[0]

    subTabs.forEach((tab, index) => {
      const isActive = index === 0

      tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
      tab.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    subPanels.forEach((panel) => {
      panel.hidden = !firstSubTab || panel.dataset.subPanelFor !== firstSubTab.dataset.subTab
    })
  }

  resetTabs () {
    const firstTopTab = this.topTabs[0]

    this.topTabs.forEach((tab, index) => {
      const isActive = index === 0

      tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
      tab.setAttribute('tabindex', isActive ? '0' : '-1')
    })

    this.topPanels.forEach((panel) => {
      panel.hidden = !firstTopTab || panel.dataset.panelFor !== firstTopTab.dataset.topTab
      this.resetSubTabs(panel)
    })
  }

  focusActiveTopTab () {
    const activeTab = this.topTabs.find((tab) => tab.getAttribute('aria-selected') === 'true')
    const focusTarget = activeTab || this.getFocusableElements()[0]

    if (focusTarget && typeof focusTarget.focus === 'function') {
      focusTarget.focus()
    }
  }

  syncTriggerState () {
    if (!this.trigger) return

    const openLabel = this.trigger.dataset.mobileNavOpenLabel || 'Open menu'
    const closeLabel = this.trigger.dataset.mobileNavCloseLabel || 'Close menu'

    this.trigger.setAttribute('aria-label', this.isOpen ? closeLabel : openLabel)
    this.trigger.classList.toggle('is-active', this.isOpen)
  }

  getFocusableElements () {
    return Array.from(this.root.querySelectorAll(this.focusableSelector)).filter((element) => {
      if (element.hidden) return false
      if (element.closest('[hidden]')) return false
      return !element.hasAttribute('disabled')
    })
  }

  trapFocus (event) {
    const focusable = this.getFocusableElements()

    if (focusable.length === 0) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  closeOtherOverlays () {
    if (!store || !store.state) return

    if (store.state.global && store.state.global.searchOpened) {
      store.commit('global/closeSearch')
    }

    if (store.state.cart && store.state.cart.open) {
      store.commit('cart/closeCart')
    }
  }

  onTriggerClick (event) {
    event.preventDefault()

    if (store && store.state && store.state.global && store.state.global.searchOpened) {
      store.commit('global/closeSearch')
      return
    }

    if (this.isOpen) {
      this.close()
      return
    }

    this.open()
  }

  onRootClick (event) {
    const topTab = event.target.closest('[data-top-tab]')
    const subTab = event.target.closest('[data-sub-tab]')

    if (topTab && this.root.contains(topTab)) {
      event.preventDefault()
      this.activateTopTab(topTab)
      return
    }

    if (subTab && this.root.contains(subTab)) {
      event.preventDefault()
      this.activateSubTab(subTab)
    }
  }

  onRootKeydown (event) {
    const tab = event.target.closest('[role="tab"]')

    if (!tab || !this.root.contains(tab)) return

    const tabList = tab.closest('[role="tablist"]')

    if (!tabList) return

    const tabs = Array.from(tabList.querySelectorAll('[role="tab"]'))
    const currentIndex = tabs.indexOf(tab)

    if (currentIndex === -1) return

    let nextIndex = null

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1
    }

    if (nextIndex === null) return

    event.preventDefault()

    const nextTab = tabs[nextIndex]

    if (nextTab.matches('[data-top-tab]')) {
      this.activateTopTab(nextTab, { focus: true })
    } else if (nextTab.matches('[data-sub-tab]')) {
      this.activateSubTab(nextTab, { focus: true })
    }
  }

  onDocumentKeydown (event) {
    if (!this.isOpen) return

    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key === 'Tab') {
      this.trapFocus(event)
    }
  }

  onOverlayOpen (event) {
    if (!event.detail || event.detail.source === 'mobile-nav' || !this.isOpen) return

    this.close({ restoreFocus: false })
  }

  onMediaChange (event) {
    if (event.matches) {
      if (this.sliderRaf) {
        window.cancelAnimationFrame(this.sliderRaf)
        this.sliderRaf = null
      }

      this.destroySliders()
      this.close({ restoreFocus: false, skipHidden: true })
    }
  }

  onOpenLevelRequest (event) {
    if (!event.detail || this.mediaQuery.matches) return

    const level = Number(event.detail.level)
    const restoreFocus = typeof event.detail.restoreFocus === 'undefined'
      ? false
      : event.detail.restoreFocus

    if (level !== 1) return

    this.resetTabs()

    if (!this.isOpen) {
      this.open()
    } else {
      this.focusActiveTopTab()
    }

    if (!restoreFocus) {
      this.previouslyFocused = null
    }
  }
}

export default (root) => {
  if (!root) return null

  const instance = new RedesignNavMobile(root)
  instance.init()
  return instance
}
